// Dokumente (Screen eigentuemer/09): nur vom Büro mit „für Eigentümer sichtbar“ freigegebene Unterlagen
// zum gewählten Objekt (plus persönliche ohne Objekt), gruppiert. Download über portal-datei (prüft bei jedem Abruf).
import { useState } from 'react';
import { CircleNotch, DownloadSimple, FileText, FolderSimple, Receipt, UsersThree } from '@phosphor-icons/react';
import type { Icon } from '@phosphor-icons/react';
import { FehlerHinweis, IconChip, LeerZustand, farbKlasse, type PortalFarbe } from '../../components/portal/bausteine';
import { dateiKuerzel, dokMeta } from '../../lib/portal/mieterE5';
import { gruppiereEigDokumente, type EigDokGruppe, type EigDokument } from '../../lib/portal/eigentuemerE7';

const GRUPPEN_STIL: Record<EigDokGruppe, { icon: Icon; farbe: PortalFarbe }> = {
  versammlung: { icon: UsersThree, farbe: 'gemein' },
  abrechnungen: { icon: Receipt, farbe: 'konto' },
  grundlagen: { icon: FileText, farbe: 'wohnung' },
  weitere: { icon: FolderSimple, farbe: 'sonst' },
};

export default function EigentuemerDokumente({ dokumente, herunterladen }: {
  dokumente: EigDokument[];
  herunterladen: (d: EigDokument) => Promise<void>;
}) {
  const [laedt, setLaedt] = useState<string | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const gruppen = gruppiereEigDokumente(dokumente);

  const laden = async (d: EigDokument) => {
    if (laedt) return;
    setLaedt(d.id); setFehler(null);
    try { await herunterladen(d); } catch (e) { setFehler((e as Error).message); } finally { setLaedt(null); }
  };

  return (
    <div className="p-rein" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      {fehler && <FehlerHinweis text={fehler} onSchliessen={() => setFehler(null)} />}
      {gruppen.length === 0 ? <LeerZustand icon={FolderSimple} text="Zurzeit sind zu diesem Objekt keine Unterlagen für Sie freigegeben" /> : (
        <div className="mp-karten">
          {gruppen.map((g) => {
            const stil = GRUPPEN_STIL[g.key];
            return (
              <section key={g.key} className={`p-glas p-hebt ${farbKlasse(stil.farbe)}`} style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 12 }} aria-label={g.label}>
                <div className="p-glas-schimmer"><stil.icon size={170} weight="duotone" className="p-wasserzeichen" style={{ opacity: 0.11 }} /></div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <IconChip farbe={stil.farbe} icon={stil.icon} />
                  <h2 style={{ margin: 0, fontSize: 14, fontWeight: 800, flex: 1 }}>{g.label}</h2>
                  <span style={{ fontSize: 12, fontWeight: 800, color: 'var(--p-w45)' }}>{g.docs.length === 1 ? '1 Dokument' : `${g.docs.length} Dokumente`}</span>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  {g.docs.map((d) => (
                    <button key={d.id} type="button" className="p-zeile" disabled={!d.herunterladbar || !!laedt} onClick={() => void laden(d)}
                      data-tip={d.herunterladbar ? `${d.titel} herunterladen` : 'Bitte bei der Verwaltung anfordern'} style={{ padding: '10px 10px' }}>
                      <span aria-hidden style={{ width: 38, height: 38, flexShrink: 0, borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 800, background: 'var(--p-fehler-bg)', color: 'var(--p-fehler-text)' }}>{dateiKuerzel(d)}</span>
                      <span style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <span className="p-kuerzen" style={{ fontSize: 13.5, fontWeight: 800 }}>{d.titel}</span>
                        <span style={{ fontSize: 11.5, fontWeight: 600, color: 'var(--p-w50)' }}>{d.herunterladbar ? dokMeta(d) : `${dokMeta(d)} · bei der Verwaltung anfordern`}</span>
                      </span>
                      {laedt === d.id ? <CircleNotch size={17} weight="bold" className="animate-spin" /> : d.herunterladbar && <DownloadSimple size={17} weight="bold" style={{ color: 'var(--p-w55)' }} />}
                    </button>
                  ))}
                </div>
              </section>
            );
          })}
        </div>
      )}
    </div>
  );
}
